// This store represents some global values that apply to all Peytil posters. It's intended to consolidate the location
// of code that spans multiple posters rather than having to duplicate it across every component. However, since it only
// applies to the Peytil posters where the child needs to be resized depending on provided values, it is separated from
// the regular poster store.
export default {
  state: {},
  mutations: {},
  actions: {},

  getters: {
    // peytilScaleOf will return a translated value of the "SCALE 1:1" part of the baby's name. The reasons why these
    // are not in the translation files are partly because the poster has a separate language (locale) selector, and
    // partly because we won't translate all the scale values you can set.
    peytilScaleText (state, getters) {
      switch (getters.posterLocale) {
        case 'da': return 'SKALA 1:1 AF'
        case 'de': return 'MAßSTAB 1:1'
        case 'et': return 'MÕÕTKAVA 1:1'
        case 'es': return 'ESCALA 1:1 DE'
        case 'fr': return 'ÉCHELLE 1:1 DE'
        case 'fo': return 'MÁT 1:1 AV'
        case 'is': return 'SKALI 1:1'
        case 'it': return 'SCALA 1:1 DI'
        case 'hu': return '1:1 ARÁNYÚ'
        case 'nl': return 'SCHAAL 1:1 VAN'
        case 'nb': return 'SKALA 1:1 AV'
        case 'pl': return 'W SKALI 1:1'
        case 'pt': return 'ESCALA 1:1 DE'
        case 'fi': return 'MITTAKAAVA 1:1'
        case 'sv': return 'SKALA 1:1 AV'
        default:
          return 'SCALE 1:1 OF'
      }
    },

    peytilChildMetaData (state, getters) {
      // Example: '51 centimeters ∙ 3016 grams ∙ 13 oktober 2017 14:37'
      return getters.posterLengthFormatted + ' ∙ ' +
        getters.posterWeightFormatted + ' ∙ ' +
        getters.posterBirthDate.format('LL') + ' ' +
        getters.posterBirthTimeFormatted
    },

    // childHeight represents the height in millimeters by the actual drawing of the baby within the poster. It is
    // calculated by knowing that the poster is 500mm tall (or 506 if with white frame) and then just measuring how tall
    // the baby is within the poster.
    //
    // The easiest way to calculate this value is to measure the height of the entire SVG file in the browser, becoming,
    // let's say, 796px tall. After that, measure the height of the baby itself, which becomes, let's say 570px. That's
    // 570/796 --> ~0.7161. 71.61% of 500mm (the final poster physical height) is ~358, which means the natural baby
    // size within the poster is 358mm tall (path scale 1.0).
    //
    // If the child size in the poster is set to 215mm, that's 215/358 --> 60%, executed by applying
    // "transform: scale(0.6)". For that calculation, which also requires "transform: translate" as the child is not
    // positioned according to the poster's center, see the "childTransform" method below.
    peytilChildHeight (state) {
      return 500
    },

    // scale will return a value to be used with the CSS "transform" property. It's required since the Peytil
    // posters represent the actual physical size of the baby at birth, so the size of the child on the poster must be
    // absolutely correct.
    //
    // The value should apply to the "path" element that represents the drawing of the baby, making sure that it's the
    // correct height (i.e. the "length" value from the poster store) and that the child is properly centered in the
    // poster frame, as per the original design.
    //
    // For reference, in the original design, the baby is 50cm tall, and out posters are 70cm tall. So scale 1 is a
    // 50cm tall baby.
    peytilScale (state, getters) {
      // Obtain the baby's length, as per what has been provided by the user (see below for units).
      let childLength = getters.posterLength
      if (isNaN(childLength) || childLength === 0) {
        return ''
      }

      // The value provided in the store is either cm or inches. In any case, we have to convert them to millimeters.
      switch (getters.posterUnit) {
        case 'metric':
          childLength *= 10
          break
        case 'imperial':
          childLength *= 25.4
          break
      }

      let scale = childLength / getters.peytilChildHeight
      if (isNaN(scale)) {
        return ''
      }

      return scale
    },

    // translate will return a value usable with the SVG transform-property, and should be applied to a group-element
    // (<g />) in which the path with the baby's size should reside. It is used to make sure that the picture of the
    // baby is always centered inside the parent SVG.
    //
    // Note that in the templates provided by Peytil, there is no dedicated group element surrounding the actual baby
    // path, which means it has to be provided manually.
    //
    // @see https://stackoverflow.com/a/11671373/2670926
    peytilTranslate (state, getters) {
      // The container width is defined in the viewBox property on the outer-most SVG element in the template. We'll
      // split those in two for readability.
      let templateWidth = 1417.3
      let templateHeight = 1984.3

      let translateX = 0
      let translateY = 0
      switch (true) {
        case getters.peytilScale === 1.0:
          break
        default:
          translateX = (1 - getters.peytilScale) * (templateWidth / 2)
          translateY = (1 - getters.peytilScale) * (templateHeight / 2)
          break
      }

      return 'translate(' + translateX + ', ' + translateY + ')'
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/peytil.js